/* eslint-disable max-lines */
/* eslint-disable @scandipwa/scandipwa-guidelines/jsx-no-props-destruction */
/**
 * @author Vladislavs Zimnikovs <vladislavs.zimnikovs@scandiweb.com>
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package puma-mx
 */

import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { mapDispatchToProps as sourceMapDispatchToProps } from 'Component/Product/Product.container';
import { SIZE_CHART_POPUP_ID } from 'Component/SizeChartPopup/SizeChartPopup.config';
import { getVariantIndex } from 'Route/ProductPage/ProductPage.container';
import {
    mapStateToProps as sourceMapStateToProps,
    ProductActionsContainer as SourceContainer
} from 'SourceComponent/ProductActions/ProductActions.container';
import { setIsFooterBootomSpaceNeeded } from 'Store/HeaderAndFooter/HeaderAndFooter.action';
import { showPopup } from 'Store/Popup/Popup.action';
import { updateUrlRewrite } from 'Store/UrlRewrites/UrlRewrites.action';
import history from 'Util/History';
import { validateGroup } from 'Util/Validator';

import ProductActionsComponent from './ProductActions.component';

/** @namespace Scandipwa/Component/ProductActions/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    ...sourceMapStateToProps(state),
    default_country: state.ConfigReducer.default_country,
    urlRewrite: state.UrlRewritesReducer.urlRewrite,
    activeOverlay: state.OverlayReducer.activeOverlay,
    promoExcludedNotice: state.ConfigReducer.promo_excluded_notice,
    configurableProductsAttrs: state.ConfigReducer.configurableProductsAttrs,
    tabbyWidgetPdp: state.ConfigReducer.getTabbyWidgetPdp,
    tamaraWidgetPdp: state.ConfigReducer.getTamaraWidgetPdp
});

/** @namespace Scandipwa/Component/ProductActions/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    ...sourceMapDispatchToProps(dispatch),
    showPopup: (payload) => dispatch(showPopup(SIZE_CHART_POPUP_ID, payload)),
    updateUrlRewrite: (urlRewrite, requestedUrl) => dispatch(updateUrlRewrite(urlRewrite, requestedUrl)),
    setIsFooterBootomSpaceNeeded:
        (isFooterBottomSpaceNeeded) => dispatch(
            setIsFooterBootomSpaceNeeded(isFooterBottomSpaceNeeded)
        )
});

/** @namespace Scandipwa/Component/ProductActions/Container */
export class ProductActionsContainer extends SourceContainer {
    static propTypes = {
        ...SourceContainer.propTypes,
        showPopup: PropTypes.func.isRequired,
        promoExcludedNotice: PropTypes.string.isRequired
    };

    containerFunctions = {
        ...this.containerFunctions,
        showOnlyIfLoaded: this.showOnlyIfLoaded.bind(this),
        getIsSizeDisabled: this.getIsSizeDisabled.bind(this),
        handleSizeChartClick: this.handleSizeChartClick.bind(this),
        updateConfigurableVariantAndQty: this.updateConfigurableVariantAndQty.bind(this),
        addToCartWithReturn: this.addToCartWithReturn.bind(this),
        getPreviewProduct: this.getPreviewProduct.bind(this),
        getGalleryProduct: this.getGalleryProduct.bind(this)
    };

    containerProps() {
        const { parameters, promoExcludedNotice } = this.props;

        return {
            ...super.containerProps(),
            parameters,
            promoExcludedNotice
        };
    }

    showOnlyIfLoaded(expression, content, placeholder = content) {
        const { areDetailsLoaded } = this.props;

        if (expression) {
            return content;
        }

        if (!areDetailsLoaded) {
            return placeholder;
        }

        if (areDetailsLoaded && !expression) {
            return null;
        }

        return content;
    }

    getNewParameters(parameters, key, value = '') {
        return {
            ...parameters,
            [key]: value.toString()
        };
    }

    /**
     * Overridden to add updateUrl()
     * @param key
     * @param value
     */
    updateConfigurableVariant(key, value, checkEmptyValue = false) {
        const { parameters: prevParameters } = this.state;
        const { search } = window.location;
        const urlParams = new URLSearchParams(search);
        const newParameters = this.getNewParameters(prevParameters, key, value);

        if (!newParameters.color && urlParams.has('color')) {
            newParameters.color = urlParams.get('color');
        }

        const { [key]: oldValue, ...currentParameters } = newParameters;
        const parameters = oldValue === '' && checkEmptyValue ? currentParameters : newParameters;

        this.setState({ parameters });

        const { updateUrl, product: { variants } } = this.props;
        const { selectedProduct } = this.state;

        const newIndex = getVariantIndex(variants, parameters);

        const newProduct = newIndex === -1 ? null : variants[newIndex];

        if (newProduct !== selectedProduct) {
            this.setState({
                selectedProduct: newProduct,
                parameters
            });
        }

        updateUrl(key, value, parameters, newProduct);
    }

    async updateConfigurableVariantAndQty(key, value) {
        const { parameters } = this.props;
        await this.updateConfigurableVariant(key, value);

        if (key === 'color' && parameters.size) {
            this.updateConfigurableVariant('size', parameters.size);
        }

        this.setQuantity(1);
    }

    handleSizeChartClick() {
        const { showPopup } = this.props;
        const { location: { search } } = window;

        this.setState({ search });

        showPopup({
            title: __('SIZE GUIDE')
        });
    }

    // The product to show when user land on PDP and no size is yet selected.
    // Used for style number, price, etc...
    getPreviewProduct() {
        const {
            parameters,
            parameters: { color },
            product: { variants = [] } = {}
        } = this.props;

        if (color && Object.keys(parameters).length === 1) {
            const index = variants.findIndex(({ color: vColor }) => +color === +vColor);

            if (index !== -1) {
                return variants[index];
            }
        }

        return false;
    }

    getGalleryProduct() {
        const {
            parameters: { color },
            product: { variants = [] } = {}
        } = this.props;

        if (color) {
            const index = variants.findIndex(({ color: vColor }) => +color === +vColor);

            if (index !== -1) {
                return variants[index];
            }
        }

        return false;
    }

    validateAttributes() {
        const { showError } = this.props;
        const { location: { search } } = history;
        const params = new URLSearchParams(search);

        if (!params.get('color')) {
            showError(__('Please select color'));
        }

        if (!params.get('size')) {
            showError(__('Please select size'));
        }
    }

    hasError() {
        const { errorMessages, errorFields, values } = validateGroup(this.validator);

        if (
            errorFields
            || errorMessages
            || this.validateConfigurableProduct()
            || this.filterAddToCartFileErrors(values)
        ) {
            this.scrollOptionsIntoView();
            this.setState({ addToCartTriggeredWithError: true });
            this.validateAttributes();

            return true;
        }

        return false;
    }

    getIsSizeDisabled({ attribute_value }) {
        const { parameters: { color }, product: { variants } } = this.props;

        const withStock = variants.findIndex(
            ({
                color: vColor,
                salable_qty,
                attributes: {
                    size: { attribute_value: v_attribute_value }
                }
            }) => (+color === +vColor && +attribute_value === +v_attribute_value && +salable_qty > 0)
        );

        return withStock === -1;
    }

    normalizeSelectedOptions = (options, firstAttr) => {
        if (parseInt(atob(options[1]).split('/')[1], 10) === firstAttr) {
            return [options[1], options[0]];
        }

        return options;
    };

    async addToCartWithReturn() {
        this.updateSelectedValues();
        const { showError, configurableProductsAttrs } = this.props;
        const { search } = window.location;
        const urlParams = new URLSearchParams(search);
        const {
            selectedProduct: {
                type_id,
                style_number
            }
        } = this.state;

        if (this.hasError()) {
            if (urlParams.has('_sgm_campaign') && urlParams.has('color') && urlParams.has('size')) {
                const { parameters } = this.props;
                await this.setState({ parameters });
            } else {
                return Promise.reject();
            }
        }

        const { addProductToCart, cartId } = this.props;
        const products = this.getMagentoProduct();
        const { color_attr_id } = configurableProductsAttrs;

        products.map((product) => {
            if (type_id === 'simple' && style_number) {
                // eslint-disable-next-line no-param-reassign
                product.sku = style_number;
            }

            // eslint-disable-next-line no-param-reassign
            product.selected_options = this.normalizeSelectedOptions(product.selected_options, color_attr_id);

            return product;
        });

        await addProductToCart({ products, cartId })
            .catch(
                /** @namespace Scandipwa/Component/ProductActions/Container/ProductActionsContainer/addToCartWithReturn/addProductToCart/catch */
                (error) => {
                    if (error) {
                        showError(error);
                    }

                    return Promise.reject();
                }
            );

        return Promise.resolve();
    }

    render() {
        return (
            <ProductActionsComponent
              { ...this.props }
              { ...this.state }
              { ...this.containerProps() }
              { ...this.containerFunctions }
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductActionsContainer);
